/**
 * Terms & Conditions copy — the live page's own `terms.*` translation keys.
 *
 * ⚠️  THIS MODULE HOLDS KEYS, NOT ENGLISH. Every string below is a translation key
 * that LegalDoc resolves (see @Components/LegalDoc/types).
 *
 * ── Why keys and not the transcribed text ───────────────────────────────────
 * It used to hold the English transcribed verbatim from https://amberstudent.com/terms.
 * That text is stored in Tolgee under `terms.*` — already reviewed in all seven
 * languages — so translating this page means POINTING AT those entries rather than
 * adding a second `terms.v2.*` copy of the same document. Legal prose is not ours to
 * re-translate, and two copies of a terms document is two copies to keep in step.
 *
 * ── What that decides about the structure ──────────────────────────────────
 * The run splits follow the stored keys' granularity, not where a sentence would like
 * an anchor:
 *
 *   · Clause 7 is `…paragraph1.before` + the Stripe link + `…paragraph1.after`, which
 *     is exactly how the stored keys split it. `terms.section7.paragraph2` ("Deposit.")
 *     is the tail of that same sentence upstream, so it is appended rather than being
 *     its own paragraph — the one presentation fix this file used to make in English is
 *     now made by joining two keys.
 *   · The intro's site URL is NOT a link run: `terms.intro.paragraph1` carries it mid
 *     sentence in every locale. LegalDoc's AUTOLINKS pass turns it into an anchor, so
 *     the design keeps its link without the paragraph being cut into three.
 *   · Clause titles come from `terms.v2.section*.title` — the stored titles with the
 *     leading number and trailing colon removed, per locale, because LegalDoc derives
 *     the number from array order.
 *
 * ── The one clause with no stored key ──────────────────────────────────────
 * Clause 15, "Consent to Personal Data Collection & Processing", is not on the live
 * page and so has no `terms.section*` entry. It is the only clause here with a new
 * `terms.v2.*` key, and the only one whose translations were not already reviewed.
 *
 * The company name and registered address stay literal: `t()` returns a
 * non-namespaced string unchanged, and neither is translated.
 */

import { Inline, InlineLink, LegalDocContent, Section } from "@Components/LegalDoc/types";

/** Stored verbatim, not translated — the support mailbox. */
const EMAIL: InlineLink = {
  text: "contact@amberstudent.com",
  href: "mailto:contact@amberstudent.com",
};

const INTRO: Inline[][] = [
  // The site URL inside this paragraph is linked by LegalDoc's AUTOLINKS pass.
  ["terms.intro.paragraph1"],
  ["terms.intro.paragraph2"],
];

const CALLOUT = "terms.intro.paragraph3";

const SECTIONS: Section[] = [
  {
    title: "terms.v2.section1.title",
    blocks: [{ kind: "para", content: ["terms.section1.content"] }],
  },
  {
    title: "terms.v2.section2.title",
    blocks: [
      {
        kind: "para",
        content: [
          "terms.section2.content",
          " ",
          { text: "terms.v2.section2.companyname", strong: true },
          " ",
          "terms.section2.content2",
          " 40 E Main St #1215 Newark DE, USA",
        ],
      },
    ],
  },
  {
    title: "terms.v2.section3.title",
    blocks: [
      { kind: "para", content: ["terms.section3.paragraph1"] },
      { kind: "para", content: ["terms.section3.paragraph2"] },
      { kind: "para", content: ["terms.section3.paragraph3"] },
    ],
  },
  {
    title: "terms.v2.section4.title",
    blocks: [
      { kind: "para", content: ["terms.section4.paragraph1"] },
      { kind: "para", content: ["terms.section4.paragraph2"] },
      { kind: "para", content: ["terms.section4.paragraph3"] },
      {
        kind: "para",
        content: ["terms.section4.paragraph4", " ", EMAIL, ". ", "terms.section4.paragraph4.after"],
      },
    ],
  },
  {
    title: "terms.v2.section5.title",
    blocks: [
      { kind: "para", content: ["terms.section5.paragraph1"] },
      { kind: "para", content: ["terms.section5.paragraph2"] },
      { kind: "para", content: ["terms.section5.paragraph3"] },
      { kind: "para", content: ["terms.section5.paragraph4"] },
    ],
  },
  {
    title: "terms.v2.section6.title",
    blocks: [
      { kind: "para", content: ["terms.section6.paragraph1"] },
      { kind: "para", content: ["terms.section6.paragraph2"] },
      { kind: "para", content: ["terms.section6.paragraph3"] },
      { kind: "para", content: ["terms.section6.paragraph4"] },
    ],
  },
  {
    title: "terms.v2.section7.title",
    blocks: [
      {
        kind: "para",
        content: [
          "terms.section7.paragraph1.before",
          {
            text: "terms.section7.paragraph1.link",
            href: "https://stripe.com/gb/customers",
            newTab: true,
          },
          // Rejoined: upstream splits this sentence across `…paragraph1.after` and
          // `…paragraph2` ("Deposit."), which reads as a stray one-word paragraph.
          "terms.section7.paragraph1.after",
          " ",
          "terms.section7.paragraph2",
        ],
      },
    ],
  },
  {
    title: "terms.v2.section8.title",
    blocks: [{ kind: "para", content: ["terms.section8.content"] }],
  },
  {
    title: "terms.v2.section9.title",
    blocks: [{ kind: "para", content: ["terms.section9.content"] }],
  },
  {
    title: "terms.v2.section10.title",
    blocks: [
      { kind: "para", content: ["terms.section10.paragraph1"] },
      { kind: "para", content: ["terms.section10.paragraph2"] },
      { kind: "para", content: ["terms.section10.paragraph3"] },
      { kind: "para", content: ["terms.section10.paragraph4"] },
      { kind: "para", content: ["terms.section10.paragraph5"] },
      { kind: "para", content: ["terms.section10.paragraph6", " ", EMAIL, "."] },
    ],
  },
  {
    title: "terms.v2.section11.title",
    blocks: [
      { kind: "para", content: ["terms.section11.paragraph1"] },
      { kind: "para", content: ["terms.section11.paragraph2"] },
      {
        kind: "para",
        content: [
          {
            text: "http://www.google.com/intl/en/help/terms_maps.html",
            href: "http://www.google.com/intl/en/help/terms_maps.html",
            newTab: true,
          },
          " and ",
          {
            text: "https://developers.google.com/maps/terms",
            href: "https://developers.google.com/maps/terms",
            newTab: true,
          },
          ".",
        ],
      },
      { kind: "para", content: ["terms.section11.paragraph3"] },
    ],
  },
  {
    title: "terms.v2.section12.title",
    blocks: [{ kind: "para", content: ["terms.section12.content"] }],
  },
  {
    title: "terms.v2.section13.title",
    blocks: [{ kind: "para", content: ["terms.section13.content"] }],
  },
  {
    title: "terms.v2.section14.title",
    blocks: [{ kind: "para", content: ["terms.section14.content"] }],
  },
  {
    // The only clause the live page does not carry — see the note at the top.
    title: "terms.v2.section15.title",
    blocks: [{ kind: "para", content: ["terms.v2.section15.content"] }],
  },
  {
    title: "terms.v2.section16.title",
    blocks: [{ kind: "para", content: ["terms.section15.content"] }],
  },
];

const TERMS_CONTENT: LegalDocContent = {
  lead: "terms.heading",
  intro: INTRO,
  callout: CALLOUT,
  sections: SECTIONS,
};

export default TERMS_CONTENT;
